// @file Store for the new-signup recommended actions card on the Getting started tab: whether it's shown, from the
// dashboard starting state, and the card Rails picks (GET /api/1/recommended-actions-card), which reloads and
// completions replace.
import { captureFetchException } from '@@/bits/error_tracker'
import type { PinnedPdAction, RecommendedAction, RecommendedActionsCard } from '@@/bits/recommended_actions_card'
import { learnVideoActionKey } from '@@/bits/recommended_actions_card'
import { RecommendedActionsCard as RecommendedActionsCardApi } from '@@/dashboard/padlet_api'
import { FetchJsonStatus, HttpCode, RecommendedActionsCardExperimentVariant } from '@@/enums'
import { Ab as AbApi } from '@@/payment/padlet_api'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import type { LearnVideo } from '@@/pinia/dash_learn_store'
import { useDashLearnStore } from '@@/pinia/dash_learn_store'
import { useDashMakeStore } from '@@/pinia/dash_make_store'
import { useExperimentsStore } from '@@/pinia/experiments'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

const EXPERIMENT_NAME = 'recommended_actions_card_experiment'
type RecommendedActionsCardGoal = 'viewed_home' | 'clicked_action' | 'dismissed'
// viewed_home is the arms' shared denominator, and only the dashboard load sends it, so a failed attempt is tried
// again after each of these delays.
const VIEWED_HOME_RETRY_DELAYS_MS = [5_000, 30_000]

// The card in a response, or null for the empty payload a user with no card gets.
function cardFrom(response: JsonAPIResponse<RecommendedActionsCard>): RecommendedActionsCard | null {
  if (response.data == null || Array.isArray(response.data)) return null
  return (response.data as JsonAPIResource<RecommendedActionsCard>).attributes
}

const useDashRecommendedActionsCardStore = defineStore('dashRecommendedActionsCardStore', () => {
  const dashAccountsStore = useDashAccountsStore()
  const dashMakeStore = useDashMakeStore()
  const experimentsStore = useExperimentsStore()
  const dashLearnStore = useDashLearnStore()

  // Whether the server shows the card to this user at all; see RecommendedActions (Rails).
  const xRecommendedActionsCard = ref<boolean>(false)

  // Null until fetched, and when the server has no card for the user.
  const card = shallowRef<RecommendedActionsCard | null>(null)
  const cardStatus = ref<FetchJsonStatus>(FetchJsonStatus.NotStarted)
  // Whether a fetch has finished, so a card that's still null means the server has none for the user (a 404) or the
  // request failed. Stays true while a refresh is in flight, so the card doesn't reappear empty meanwhile.
  const hasFinishedFetch = ref<boolean>(false)
  // Nothing to show on Getting started until a later fetch brings a card.
  const hasNoCardToShow = computed<boolean>(() => hasFinishedFetch.value && card.value == null)
  // From the server, which counts active days and reloads. Only the card's analytics read these.
  const activeDay = computed<number>(() => card.value?.activeDay ?? 1)
  const reloads = computed<number>(() => card.value?.reloads ?? 0)
  // Keys completed on this page, so each is posted once.
  const completedKeys = new Set<string>()
  // viewed_home is sent once per page load. Set while the request is in flight too, so it's sent once; cleared
  // if it fails, to try again.
  const hasConvertedViewedHome = ref<boolean>(false)
  let viewedHomeRetries = 0
  // Kept for the page load rather than per component, so the view is tracked once however many times the card
  // is mounted.
  const hasTrackedView = ref<boolean>(false)
  // Until a reload's answer arrives, the card doesn't know what a further reload features, so the reload button is
  // disabled meanwhile and reloads never overlap.
  const isReloading = ref<boolean>(false)

  // Not where the user can't make padlets. In their personal account, a user at their padlet limit still
  // qualifies (the Gallery's Create prompts an upgrade), unless their tenant blocks it.
  const canMakePadlets = computed<boolean>(() =>
    dashAccountsStore.currentLibrary == null
      ? dashAccountsStore.currentUserOptions?.canMakeWallInTenant !== false
      : !dashMakeStore.cannotMakeWallInCurrentAccount,
  )
  // Not where the Gallery tab is hidden.
  const canViewGallery = computed<boolean>(
    () => dashAccountsStore.currentUserOptions?.canViewGallery === true && dashAccountsStore.canViewCrossLibraryGallery,
  )

  // Not a library the user owns or administers: that library keeps Getting started for its own onboarding, so no
  // page shows both. A school's owners and admins are only known once its memberships load, so a library waits.
  const isOutsideOnboardingLibrary = computed<boolean>(
    () =>
      dashAccountsStore.currentLibrary == null ||
      (dashAccountsStore.hasFetchedLibraryOnboardingData && !dashAccountsStore.canCurrentUserOnboardOntoLibrary),
  )

  // In the experiment, in any arm (control included), and in the current account able to make padlets and see the
  // Gallery, outside a library they own or administer. viewed_home is sent only to these users, so the arms are
  // compared over the same users.
  const isExposedInCurrentAccount = computed<boolean>(
    () =>
      xRecommendedActionsCard.value &&
      experimentsStore.recommendedActionsCardExperimentVariant != null &&
      canMakePadlets.value &&
      canViewGallery.value &&
      isOutsideOnboardingLibrary.value,
  )

  const isInCardArm = computed<boolean>(() =>
    [RecommendedActionsCardExperimentVariant.VideoFirst, RecommendedActionsCardExperimentVariant.GalleryFirst].includes(
      experimentsStore.recommendedActionsCardExperimentVariant as RecommendedActionsCardExperimentVariant,
    ),
  )

  // Exposed (above) and in an arm that shows the card. The dashboard decides which pages show it.
  const xRecommendedActionsCardInCurrentAccount = computed<boolean>(
    () => isExposedInCurrentAccount.value && isInCardArm.value,
  )

  function initialize(): void {
    const startingState = (window.ww?.vueStartingState ?? {}) as { showRecommendedActionsCard?: boolean }
    xRecommendedActionsCard.value = startingState.showRecommendedActionsCard === true
  }

  // Once per page load. A 404 means the server has no card for the user, which hides it; any other failure is reported
  // and hides it too.
  async function fetchCard(): Promise<void> {
    if (cardStatus.value !== FetchJsonStatus.NotStarted) return
    await requestCard()
  }

  // Again, e.g. once the teacher school form closes, since Rails picks from what it saves. A 404 hides the card; any
  // other failure is reported and keeps the card already shown.
  async function refreshCard(): Promise<void> {
    await requestCard()
  }

  async function requestCard(): Promise<void> {
    cardStatus.value = FetchJsonStatus.Fetching
    try {
      card.value = cardFrom(await RecommendedActionsCardApi.fetchCard())
      cardStatus.value = FetchJsonStatus.Completed
    } catch (e) {
      if (e?.status === HttpCode.NotFound) card.value = null
      else captureFetchException(e, { source: 'DashRecommendedActionsCardStoreFetch' })
      cardStatus.value = card.value == null ? FetchJsonStatus.Error : FetchJsonStatus.Completed
    }
    hasFinishedFetch.value = true
  }

  async function convertGoal(goal: RecommendedActionsCardGoal): Promise<void> {
    if (goal === 'viewed_home') {
      if (hasConvertedViewedHome.value) return
      hasConvertedViewedHome.value = true
    }
    try {
      await AbApi.convertAbExperimentGoal(EXPERIMENT_NAME, goal)
    } catch (e) {
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreConvertGoal', goal })
      if (goal === 'viewed_home') {
        hasConvertedViewedHome.value = false
        retryViewedHomeLater()
      }
    }
  }

  function retryViewedHomeLater(): void {
    const delay = VIEWED_HOME_RETRY_DELAYS_MS[viewedHomeRetries]
    if (delay === undefined) return
    viewedHomeRetries += 1
    setTimeout(() => void convertGoal('viewed_home'), delay)
  }

  // Posts the action, once per page, and shows the card the server answers with. Keeps the card as it is when the
  // request fails or the server has no card to answer with.
  async function complete(key: string): Promise<void> {
    if (completedKeys.has(key)) return
    completedKeys.add(key)
    try {
      card.value = cardFrom(await RecommendedActionsCardApi.complete(key)) ?? card.value
    } catch (e) {
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreComplete', key })
    }
  }

  // Completes a Learn video the user watched to the end. Completions are per user, so this checks the user, not the
  // current account, which in a library waits for its onboarding data and would drop a video that ends sooner.
  async function completeLearnVideo(video: LearnVideo): Promise<void> {
    if (!xRecommendedActionsCard.value || !isInCardArm.value) return
    await complete(learnVideoActionKey(video, dashLearnStore.featuredVideos?.[0]))
  }

  // Completes an action the user opened from the card. Only a padlet.tv recording completes when opened, since its
  // player is on another site and can't report the end. A Learn video completes once it plays to the end
  // (completeLearnVideo), and a template once a padlet is made from it, in Rails.
  async function completeOpenedAction(action: RecommendedAction | PinnedPdAction): Promise<void> {
    if (action.kind !== 'padlet_tv') return
    await complete(action.key)
  }

  // Features the card's upNext straight away, keeping More to try, then saves the cursor and shows the card the
  // server answers with. When the answer has no card (a failure or the empty payload), puts back the card from before,
  // so the next reload moves on from it rather than featuring the same action again.
  async function reload(): Promise<void> {
    const shown = card.value
    if (shown == null) return
    const nextCursor = shown.nextCursor
    card.value = { ...shown, featured: shown.upNext, reloads: shown.reloads + 1 }
    isReloading.value = true
    try {
      card.value = cardFrom(await RecommendedActionsCardApi.reload(nextCursor)) ?? shown
    } catch (e) {
      card.value = shown
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreReload' })
    } finally {
      isReloading.value = false
    }
  }

  // Hides the card straight away; the server then keeps it hidden on every device and later visit. A failed
  // request keeps it hidden for the page, and it shows again on the next load.
  async function dismiss(): Promise<void> {
    xRecommendedActionsCard.value = false
    try {
      await RecommendedActionsCardApi.dismiss()
    } catch (e) {
      captureFetchException(e, { source: 'DashRecommendedActionsCardStoreDismiss' })
    }
  }

  return {
    xRecommendedActionsCard,
    isExposedInCurrentAccount,
    xRecommendedActionsCardInCurrentAccount,
    card,
    cardStatus,
    hasNoCardToShow,
    activeDay,
    reloads,
    isReloading,
    hasTrackedView,
    initialize,
    fetchCard,
    refreshCard,
    convertGoal,
    reload,
    complete,
    completeLearnVideo,
    completeOpenedAction,
    dismiss,
  }
})

export { useDashRecommendedActionsCardStore }
